import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Eye, Copy, Check } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const currencyFlags = {
  'USD': '🇺🇸',
  'GBP': '🇬🇧',
  'JPY': '🇯🇵',
  'AED': '🇦🇪',
  'CNY': '🇨🇳',
  'INR': '🇮🇳',
  'CHF': '🇨🇭',
  'CAD': '🇨🇦',
  'AUD': '🇦🇺'
};

const DashboardSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);

  const dashboardUrl = `${window.location.origin}/central`;
  
  // Fetch status options for map configuration
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/reference-data/project-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStatusOptions(response.data?.records || []);
      } catch (error) {
        console.error('Failed to fetch statuses:', error);
      }
    };
    fetchStatuses();
  }, []);

  const availableTimezones = [
    { name: 'New York', timezone: 'America/New_York' },
    { name: 'London', timezone: 'Europe/London' },
    { name: 'Paris', timezone: 'Europe/Paris' },
    { name: 'Dubai', timezone: 'Asia/Dubai' },
    { name: 'Tokyo', timezone: 'Asia/Tokyo' },
    { name: 'Sydney', timezone: 'Australia/Sydney' },
    { name: 'Los Angeles', timezone: 'America/Los_Angeles' },
    { name: 'Chicago', timezone: 'America/Chicago' },
    { name: 'Singapore', timezone: 'Asia/Singapore' },
    { name: 'Hong Kong', timezone: 'Asia/Hong_Kong' }
  ];

  const availableCurrencies = ['USD', 'GBP', 'JPY', 'AED', 'CNY', 'INR', 'CHF', 'CAD', 'AUD'];
  const availableStats = [
    { id: 'projects_assigned', label: 'Projects Assigned' },
    { id: 'hours_this_week', label: 'Hours This Week' },
    { id: 'tasks_completed', label: 'Tasks Completed' },
    { id: 'open_incidents', label: 'Unsolved Incidents' }
  ];

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/central-dashboard/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      console.log('Saving config:', config);
      const response = await axios.put(
        `${API}/api/central-dashboard/config`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Save response:', response.data);
      alert('Dashboard settings saved successfully!');
      // Refresh config to confirm save
      await fetchConfig();
    } catch (error) {
      console.error('Failed to save config:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to save settings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addClock = () => {
    if (!config) return;
    const newClock = { name: '', timezone: '' };
    const updatedConfig = {
      ...config,
      world_clocks: [...(config.world_clocks || []), newClock]
    };
    setConfig(updatedConfig);
  };

  const removeClock = (index) => {
    if (!config) return;
    const clocks = [...(config.world_clocks || [])];
    clocks.splice(index, 1);
    setConfig({ ...config, world_clocks: clocks });
  };

  const updateClock = (index, field, value) => {
    if (!config) return;
    const clocks = [...(config.world_clocks || [])];
    clocks[index][field] = value;
    setConfig({ ...config, world_clocks: clocks });
  };

  const toggleCurrency = (currency) => {
    if (!config) return;
    
    const currencies = config.exchange_rates?.currencies || [];
    const newCurrencies = currencies.includes(currency)
      ? currencies.filter(c => c !== currency)
      : [...currencies, currency];
    
    setConfig({
      ...config,
      exchange_rates: {
        ...config.exchange_rates,
        currencies: newCurrencies
      }
    });
  };

  const toggleStat = (statId) => {
    if (!config) return;
    
    const stats = config.employee_stats || [];
    const newStats = stats.includes(statId)
      ? stats.filter(s => s !== statId)
      : [...stats, statId];
    
    setConfig({ ...config, employee_stats: newStats });
  };

  const copyUrl = () => {
    try {
      // Fallback for clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = dashboardUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // If copy failed, show alert with URL
        alert(`Please copy the dashboard URL manually:\n\n${dashboardUrl}`);
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      alert(`Please copy the dashboard URL manually:\n\n${dashboardUrl}`);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading settings...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Central Dashboard Settings</h2>
        <p className="text-gray-600 mb-4">
          Configure the central dashboard displayed on large screens for real-time operations monitoring.
        </p>

        {/* Dashboard URL */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">Dashboard URL</p>
              <code className="text-sm text-blue-700 bg-white px-2 py-1 rounded">{dashboardUrl}</code>
            </div>
            <div className="flex gap-2">
              <button
                onClick={copyUrl}
                className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy URL'}
              </button>
              <a
                href={dashboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                <Eye className="w-4 h-4" />
                Preview
              </a>
            </div>
          </div>
        </div>

        {/* World Clocks */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">World Clocks</h3>
            <button
              onClick={addClock}
              className="inline-flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
            >
              <Plus className="w-4 h-4" />
              Add Clock
            </button>
          </div>
          <div className="space-y-2">
            {config?.world_clocks?.map((clock, idx) => (
              <div key={idx} className="flex gap-2">
                <input
                  type="text"
                  value={clock.name}
                  onChange={(e) => updateClock(idx, 'name', e.target.value)}
                  placeholder="City name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded"
                />
                <select
                  value={clock.timezone}
                  onChange={(e) => updateClock(idx, 'timezone', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded"
                >
                  <option value="">Select timezone</option>
                  {availableTimezones.map((tz, i) => (
                    <option key={i} value={tz.timezone}>{tz.name} ({tz.timezone})</option>
                  ))}
                </select>
                <button
                  onClick={() => removeClock(idx)}
                  className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Exchange Rates */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Exchange Rates</h3>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Base Currency</label>
            <select
              value={config?.exchange_rates?.base || 'EUR'}
              onChange={(e) => setConfig({
                ...config,
                exchange_rates: { ...config.exchange_rates, base: e.target.value }
              })}
              className="px-3 py-2 border border-gray-300 rounded"
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Display Currencies</label>
            <div className="flex flex-wrap gap-2">
              {availableCurrencies.map((currency) => (
                <button
                  key={currency}
                  onClick={() => toggleCurrency(currency)}
                  className={`px-3 py-1 rounded text-sm font-medium ${
                    config?.exchange_rates?.currencies?.includes(currency)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <span className="mr-1">{currencyFlags[currency]}</span>
                  {currency}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Employee Stats */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Employee Statistics</h3>
          <p className="text-sm text-gray-600 mb-3">Select which statistics to display for each employee</p>
          <div className="space-y-2">
            {availableStats.map((stat) => (
              <label key={stat.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config?.employee_stats?.includes(stat.id)}
                  onChange={() => toggleStat(stat.id)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-gray-700">{stat.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* RSS Feed & Ticker Colors */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">News Ticker</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RSS Feed URL</label>
              <input
                type="url"
                value={config?.rss_feed_url || ''}
                onChange={(e) => setConfig({ ...config, rss_feed_url: e.target.value })}
                placeholder="https://www.aviation24.be/feed/"
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config?.ticker_bg_color || '#B91C1C'}
                    onChange={(e) => setConfig({ ...config, ticker_bg_color: e.target.value })}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <span className="text-sm text-gray-600">{config?.ticker_bg_color || '#B91C1C'}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config?.ticker_text_color || '#FFFFFF'}
                    onChange={(e) => setConfig({ ...config, ticker_text_color: e.target.value })}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <span className="text-sm text-gray-600">{config?.ticker_text_color || '#FFFFFF'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Refresh Interval */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Auto-Refresh</h3>
          <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Interval (seconds)</label>
          <input
            type="number"
            value={config?.refresh_interval || 30}
            onChange={(e) => setConfig({ ...config, refresh_interval: parseInt(e.target.value) })}
            min="10"
            max="300"
            className="px-3 py-2 border border-gray-300 rounded"
          />
          <p className="text-xs text-gray-500 mt-1">How often to refresh live data (10-300 seconds)</p>
        </div>

        {/* Map Configuration */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Map Pin Configuration</h3>
          <p className="text-sm text-gray-600 mb-3">Select which project statuses appear on the map and their pin colors</p>
          <div className="space-y-2">
            {statusOptions.map((status) => {
              const mapSettings = config?.map_settings || {};
              const statusConfig = mapSettings[status.id] || { enabled: false, color: '#3B82F6' };
              
              return (
                <div key={status.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    checked={statusConfig.enabled}
                    onChange={(e) => {
                      const newSettings = {
                        ...mapSettings,
                        [status.id]: {
                          ...statusConfig,
                          enabled: e.target.checked
                        }
                      };
                      setConfig({ ...config, map_settings: newSettings });
                    }}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="flex-1 text-sm font-medium">{status.field_84_raw || status.field_84}</span>
                  {statusConfig.enabled && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600">Pin Color:</label>
                      <input
                        type="color"
                        value={statusConfig.color}
                        onChange={(e) => {
                          const newSettings = {
                            ...mapSettings,
                            [status.id]: {
                              ...statusConfig,
                              color: e.target.value
                            }
                          };
                          setConfig({ ...config, map_settings: newSettings });
                        }}
                        className="w-10 h-8 rounded border border-gray-300 cursor-pointer"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* YouTube Split View */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">YouTube Split View</h3>
          <p className="text-sm text-gray-600 mb-3">Display a YouTube stream alongside the map</p>
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config?.split_view_enabled || false}
                onChange={(e) => setConfig({ ...config, split_view_enabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Enable Split View</span>
            </label>
            
            {config?.split_view_enabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube URL</label>
                <input
                  type="url"
                  value={config?.youtube_url || ''}
                  onChange={(e) => setConfig({ ...config, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
                <p className="text-xs text-gray-500 mt-1">Enter a YouTube video or live stream URL</p>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardSettings;
